<template>
    <b-modal
        :id="modalId"
        size="lg"
        hide-footer
        centered
        no-close-on-backdrop
        @shown="onShown"
        @hidden="onHidden">
        <template #modal-header>
            <h5 class="modal-heading mb-0">Attach Contacts to Job</h5>
            <button type="button" class="close-btn" @click="close" aria-label="Close">
                <img src="/img/x.svg" width="14" alt="" />
            </button>
        </template>

        <div class="attach-contacts-content">
            <!-- Selected pills -->
            <div v-if="selectedList.length > 0" class="selected-bar px-3 pt-3 d-flex flex-wrap gap-2">
                <span
                    v-for="c in selectedList"
                    :key="`sel-${c.id}`"
                    class="selected-pill d-inline-flex align-items-center gap-1 fs-12">
                    <span
                        class="pill-avatar flex-shrink-0"
                        :style="{ background: avatarBg(c.id) }">
                        {{ initialsFor(c.name) }}
                    </span>
                    <span class="pill-name">{{ c.name }}</span>
                    <button
                        type="button"
                        class="pill-remove"
                        @click="deselect(c.id)"
                        aria-label="Remove">
                        <img src="/img/x.svg" width="10" alt="" />
                    </button>
                </span>
            </div>

            <!-- Search input -->
            <div class="position-relative p-3">
                <img
                    src="/img/search.svg"
                    class="search-icon position-absolute"
                    width="20"
                    alt="" />
                <input
                    ref="searchInput"
                    type="text"
                    class="form-control search-bar border-gray-300"
                    placeholder="Search contacts by name or email"
                    v-model="searchQuery"
                    @input="onSearchInput" />
            </div>

            <!-- Results -->
            <div v-if="isSearching" class="text-center py-4">
                <div class="spinner-border text-gray-400 spinner-border-sm" role="status">
                    <span class="sr-only">Searching...</span>
                </div>
            </div>

            <div v-else-if="!hasSearched" class="text-center py-4">
                <p class="text-muted fs-14 mb-0">Type to search your contacts</p>
            </div>

            <div v-else-if="results.length === 0" class="text-center py-4">
                <p class="text-muted fs-14 mb-0">No contacts match "{{ lastSearchedQuery }}"</p>
            </div>

            <div v-else class="contacts-scroll">
                <ul class="list-unstyled mb-0">
                    <li
                        v-for="contact in results"
                        :key="contact.id"
                        class="picker-row d-flex align-items-center gap-12 px-3 py-2"
                        @click="toggle(contact)">
                        <b-form-checkbox
                            :checked="selectedMap[contact.id] != null"
                            @change="toggle(contact)"
                            @click.native.stop />
                        <span class="picker-avatar flex-shrink-0" :style="{ background: avatarBg(contact.id) }">
                            {{ initialsFor(contact.name) }}
                        </span>
                        <div class="flex-fill min-w-0">
                            <div class="text-ashore-black fs-14 text-truncate" :title="contact.name">{{ contact.name }}</div>
                            <div class="text-gray-500 fs-12 text-truncate" :title="contact.email">{{ contact.email }}</div>
                        </div>
                    </li>
                </ul>
            </div>
        </div>

        <div class="modal-footer-row d-flex align-items-center justify-content-between gap-2 px-3 py-3 border-top border-gray-100">
            <div class="fs-12 text-gray-500">
                <template v-if="diff.added.length || diff.removed.length">
                    {{ diff.added.length }} to add, {{ diff.removed.length }} to remove
                </template>
                <template v-else>
                    {{ selectedList.length }} selected
                </template>
            </div>
            <div class="d-flex gap-2">
                <button class="btn cancel-btn fw-normal" @click="close">Cancel</button>
                <button
                    class="btn btn-success fw-normal"
                    :disabled="isSaving || (!diff.added.length && !diff.removed.length)"
                    @click="save">
                    {{ isSaving ? 'Saving…' : 'Save' }}
                </button>
            </div>
        </div>
    </b-modal>
</template>

<script>
import { debounce } from "lodash";

const PALETTE = [
    "#019256", "#4F46E5", "#DC2626", "#D97706", "#0891B2",
    "#7C3AED", "#DB2777", "#059669", "#2563EB", "#EA580C"
];

export default {
    name: "modalAttachContacts",
    props: {
        modalId: { type: String, default: "modalAttachContacts" },
        attachedContacts: { type: Array, default: () => [] },
    },
    data() {
        return {
            searchQuery: "",
            lastSearchedQuery: "",
            results: [],
            selectedMap: {},
            initialIds: new Set(),
            isSearching: false,
            hasSearched: false,
            isSaving: false,
        };
    },
    computed: {
        selectedList() {
            return Object.values(this.selectedMap);
        },
        diff() {
            const selectedIds = new Set(Object.keys(this.selectedMap).map(Number));
            const added = [];
            const removed = [];
            for (const id of selectedIds) {
                if (!this.initialIds.has(id)) added.push(id);
            }
            for (const id of this.initialIds) {
                if (!selectedIds.has(id)) removed.push(id);
            }
            return { added, removed };
        },
    },
    created() {
        this.debouncedSearch = debounce(this.runSearch, 300);
    },
    methods: {
        onShown() {
            this.searchQuery = "";
            this.lastSearchedQuery = "";
            this.results = [];
            this.hasSearched = false;
            this.isSearching = false;

            const map = {};
            const ids = new Set();
            for (const c of this.attachedContacts || []) {
                if (c && c.id != null) {
                    map[c.id] = { id: c.id, name: c.name, email: c.email };
                    ids.add(Number(c.id));
                }
            }
            this.selectedMap = map;
            this.initialIds = ids;

            this.$nextTick(() => {
                if (this.$refs.searchInput) this.$refs.searchInput.focus();
            });
        },
        onHidden() {
            this.searchQuery = "";
            this.results = [];
            this.selectedMap = {};
            this.initialIds = new Set();
            this.hasSearched = false;
        },
        onSearchInput() {
            const q = this.searchQuery.trim();
            if (!q) {
                this.debouncedSearch.cancel();
                this.results = [];
                this.hasSearched = false;
                this.isSearching = false;
                return;
            }
            this.isSearching = true;
            this.debouncedSearch(q);
        },
        async runSearch(q) {
            const wildcard = `%${q}%`;
            try {
                const res = await this.$A.ApproverService.All(wildcard, wildcard);
                if (this.searchQuery.trim() !== q) return; // stale
                this.results = Array.isArray(res) ? res : [];
                this.lastSearchedQuery = q;
                this.hasSearched = true;
            } catch (e) {
                console.error("Contact search failed:", e);
                this.results = [];
                this.hasSearched = true;
            } finally {
                if (this.searchQuery.trim() === q) this.isSearching = false;
            }
        },
        toggle(contact) {
            const id = contact.id;
            const next = { ...this.selectedMap };
            if (next[id]) delete next[id];
            else next[id] = { id, name: contact.name, email: contact.email };
            this.selectedMap = next;
        },
        deselect(id) {
            const next = { ...this.selectedMap };
            delete next[id];
            this.selectedMap = next;
        },
        close() {
            this.$bvModal.hide(this.modalId);
        },
        save() {
            const { added, removed } = this.diff;
            if (!added.length && !removed.length) return;
            this.$emit("save", {
                added,
                removed,
                selectedContacts: this.selectedList,
            });
            this.close();
        },
        initialsFor(name) {
            if (!name) return "?";
            return name
                .split(/\s+/)
                .filter(Boolean)
                .slice(0, 2)
                .map((s) => s[0])
                .join("")
                .toUpperCase();
        },
        avatarBg(id) {
            const n = Number(id) || 0;
            return PALETTE[n % PALETTE.length];
        },
    },
};
</script>

<style scoped>
.modal-heading {
    color: #3A3A3A;
    font-size: 18px;
    font-weight: 500;
}
.close-btn {
    background: transparent;
    border: 0;
    cursor: pointer;
    opacity: 0.6;
    padding: 4px;
}
.close-btn:hover { opacity: 1; }

.selected-bar {
    border-bottom: 1px solid #F4F4F6;
    padding-bottom: 12px;
}
.selected-pill {
    background: #F3F4F6;
    border-radius: 999px;
    padding: 3px 8px 3px 3px;
    color: #374151;
    line-height: 1;
}
.pill-avatar {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    color: #FFFFFF;
    font-size: 10px;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    letter-spacing: -0.2px;
}
.pill-name {
    max-width: 180px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.pill-remove {
    background: transparent;
    border: 0;
    padding: 2px 4px;
    cursor: pointer;
    opacity: 0.6;
}
.pill-remove img {
    filter: grayscale(100%) brightness(0.6);
}
.pill-remove:hover {
    opacity: 1;
}

.search-icon {
    left: 28px;
    top: 50%;
    transform: translateY(-50%);
    opacity: 0.6;
    pointer-events: none;
}
.search-bar { padding-left: 40px; }

.contacts-scroll {
    max-height: 360px;
    overflow-y: auto;
}

.picker-row {
    cursor: pointer;
    transition: background 0.12s ease;
}
.picker-row + .picker-row {
    border-top: 1px solid #F4F4F6;
}
.picker-row:hover { background: #F9FAFB; }

.picker-avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    color: #FFFFFF;
    font-size: 12px;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    letter-spacing: -0.2px;
}

.cancel-btn { background-color: #EEF2F5 !important; }
</style>
